import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Card, Grid, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";
import { useCreateAntecedenteMutation } from "services/escuela/antecedentes_estudiantil";
import { useGetNivelesEstudioQuery } from "services/escuela/nomencladores/nivel_estudio.service";
import { useGetEntidadesFederativasQuery } from "services/administracion/entidad_federativa.service";

const validationSchema = Yup.object().shape({
  estudiante: Yup.string().required("El estudiante es requerido"),
  institucion_procedencia: Yup.string()
    .required("La institución procedencia es requerida")
    .max(256, "Máximo 256 caracteres"),
  tipo_estudio_antecedente: Yup.string().required(
    "El tipo de estudio es requerido",
  ),
  entidad_federativa_antecedente: Yup.string().required(
    "La entidad federativa es requerida",
  ),
  fecha_inicio: Yup.date().required("La fecha de inicio es requerida"),
  fecha_terminacion: Yup.date().required(
    "La fecha de terminación es requerida",
  ),
  no_cedula: Yup.string().nullable().max(50, "Máximo 50 caracteres"),
});

const initialValues = {
  estudiante: "",
  institucion_procedencia: "",
  tipo_estudio_antecedente: "",
  entidad_federativa_antecedente: "",
  fecha_inicio: "",
  fecha_terminacion: "",
  no_cedula: "",
};

const AntecedenteForm = ({
  estudianteUrl,
  nombreEstudiante,
  curpEstudiante,
  onSuccess,
  onCancel,
}) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const [createAntecedente, { isLoading }] = useCreateAntecedenteMutation();

  const { data: nivelesEstudioData } = useGetNivelesEstudioQuery();
  const nivelesEstudio =
    nivelesEstudioData?.results || nivelesEstudioData || [];

  const { data: entidadesFederativasData } = useGetEntidadesFederativasQuery(
    {
      shape: {
        id_entidad: "id_entidad",
        entidad: "entidad",
        abr: "abr",
        nid: "nid",
        url: "url",
      },
    },
    { refetchOnReconnect: true },
  );

  const entidadesFederativas =
    entidadesFederativasData?.results || entidadesFederativasData || [];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        fecha_inicio: values.fecha_inicio || null,
        fecha_terminacion: values.fecha_terminacion || null,
        institucion_procedencia: values.institucion_procedencia || null,
        no_cedula: values.no_cedula || null,
        tipo_estudio_antecedente: values.tipo_estudio_antecedente || null,
        entidad_federativa_antecedente:
          values.entidad_federativa_antecedente || null,
      };

      await createAntecedente(payload).unwrap();

      enqueueSnackbar(
        `Antecedente creado correctamente${
          nombreEstudiante ? ` al estudiante ${nombreEstudiante}` : ""
        }`,
        { variant: "success" },
      );
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error al crear el antecedente", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MDBox>
      <MDBox mb={2}>
        <MDTypography
          variant="h5"
          fontWeight="medium"
          color={darkMode ? "white" : "black"}
        >
          Nuevo Antecedente Académico
        </MDTypography>
        {nombreEstudiante && (
          <MDTypography variant="subtitle2" color={darkMode ? "white" : "dark"}>
            Estudiante: {nombreEstudiante}
          </MDTypography>
        )}
        {curpEstudiante && (
          <MDTypography variant="subtitle2" color={darkMode ? "white" : "dark"}>
            CURP: {curpEstudiante}
          </MDTypography>
        )}
      </MDBox>

      <Formik
        initialValues={{ ...initialValues, estudiante: estudianteUrl || "" }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, isSubmitting }) => (
          <Form autoComplete="off">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormField
                  label="Institución de Procedencia"
                  name="institucion_procedencia"
                  value={values.institucion_procedencia}
                  onChange={handleChange}
                  onInput={(e) =>
                    (e.target.value = ("" + e.target.value).toUpperCase())
                  }
                  error={
                    errors.institucion_procedencia &&
                    touched.institucion_procedencia
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormField
                  select
                  label="Tipo de Estudio"
                  name="tipo_estudio_antecedente"
                  value={values.tipo_estudio_antecedente}
                  onChange={handleChange}
                  error={
                    errors.tipo_estudio_antecedente &&
                    touched.tipo_estudio_antecedente
                  }
                >
                  <MenuItem value="">
                    <em>Seleccione una opción</em>
                  </MenuItem>
                  {nivelesEstudio?.map((nivel) => (
                    <MenuItem key={nivel.id} value={nivel.url}>
                      {nivel.descripcion}
                    </MenuItem>
                  ))}
                </FormField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormField
                  select
                  label="Entidad Federativa"
                  name="entidad_federativa_antecedente"
                  value={values.entidad_federativa_antecedente}
                  onChange={handleChange}
                  error={
                    errors.entidad_federativa_antecedente &&
                    touched.entidad_federativa_antecedente
                  }
                >
                  <MenuItem value="">
                    <em>Seleccione una opción</em>
                  </MenuItem>
                  {entidadesFederativas?.map((entidad) => (
                    <MenuItem key={entidad.id_entidad} value={entidad.url}>
                      {entidad.entidad}
                    </MenuItem>
                  ))}
                </FormField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormField
                  type="date"
                  label="Fecha de Inicio"
                  name="fecha_inicio"
                  value={values.fecha_inicio}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={errors.fecha_inicio && touched.fecha_inicio}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormField
                  type="date"
                  label="Fecha de Terminación"
                  name="fecha_terminacion"
                  value={values.fecha_terminacion}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={errors.fecha_terminacion && touched.fecha_terminacion}
                />
              </Grid>

              <Grid item xs={12}>
                <FormField
                  label="Número de Cédula"
                  name="no_cedula"
                  value={values.no_cedula}
                  onChange={handleChange}
                  onInput={(e) =>
                    (e.target.value = ("" + e.target.value).toUpperCase())
                  }
                  error={errors.no_cedula && touched.no_cedula}
                />
              </Grid>

              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <MDButton
                  onClick={onCancel}
                  color="accent"
                  variant="outlined"
                  sx={{ mr: 1 }}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  type="submit"
                  variant="contained"
                  color="accent"
                  disabled={isSubmitting || isLoading}
                >
                  {isLoading ? "Guardando..." : "Guardar"}
                </MDButton>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </MDBox>
  );
};

AntecedenteForm.propTypes = {
  estudianteUrl: PropTypes.string,
  nombreEstudiante: PropTypes.string,
  curpEstudiante: PropTypes.string,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default AntecedenteForm;
